import React, { FunctionComponent, ReactNode, useEffect, useRef, useState } from "react";
import { Check, ChevronDown, LucideIcon } from "lucide-react";
import "./SelectMenu.scss";

export interface SelectOption<V extends string = string> {
    value: V;
    label: ReactNode;
    /** Texto usado no trigger quando `label` e um no complexo. */
    text?: string;
    icon?: LucideIcon;
    disabled?: boolean;
}

interface SelectMenuProps<V extends string = string> {
    options: SelectOption<V>[];
    value: V;
    onChange: (value: V) => void;
    /** Exibido quando nada esta selecionado. */
    placeholder?: string;
    ariaLabel?: string;
    className?: string;
    disabled?: boolean;
}

/**
 * Substituto do <select> nativo.
 *
 * O popup do select nativo e desenhado pelo sistema operacional: nem a posicao
 * nem o tamanho da fonte sao controlaveis por CSS, e em janela estreita ele
 * abre deslocado e minusculo. Aqui a lista e um <ul> ancorado no trigger.
 */
function SelectMenu<V extends string>({
    options,
    value,
    onChange,
    placeholder = "Selecione",
    ariaLabel,
    className = "",
    disabled = false,
}: SelectMenuProps<V>) {

    const [open, setOpen]               = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);
    const rootRef                       = useRef<HTMLDivElement>(null);

    const selectedIndex = options.findIndex((o) => o.value === value);
    const selected      = selectedIndex >= 0 ? options[selectedIndex] : undefined;

    useEffect(() => {
        if (open) setActiveIndex(Math.max(0, selectedIndex));
    }, [open, selectedIndex]);

    // Fecha ao clicar fora e ao rolar: a lista e ancorada no trigger, entao
    // rolar com ela aberta a deixaria solta no meio da tela.
    useEffect(() => {
        if (!open) return;

        const onPointerDown = (e: MouseEvent | TouchEvent) => {
            if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
        };
        const close = () => setOpen(false);

        document.addEventListener("mousedown", onPointerDown);
        document.addEventListener("touchstart", onPointerDown);
        window.addEventListener("scroll", close, true);
        window.addEventListener("resize", close);
        return () => {
            document.removeEventListener("mousedown", onPointerDown);
            document.removeEventListener("touchstart", onPointerDown);
            window.removeEventListener("scroll", close, true);
            window.removeEventListener("resize", close);
        };
    }, [open]);

    const commit = (opt: SelectOption<V>) => {
        if (opt.disabled) return;
        onChange(opt.value);
        setOpen(false);
    };

    /** Move o destaque pulando itens desabilitados. */
    const step = (from: number, dir: 1 | -1) => {
        for (let i = 1; i <= options.length; i++) {
            const next = (from + dir * i + options.length * i) % options.length;
            if (!options[next].disabled) return next;
        }
        return from;
    };

    const onKeyDown = (e: React.KeyboardEvent) => {
        if (disabled) return;
        switch (e.key) {
            case "ArrowDown":
            case "ArrowUp":
                e.preventDefault();
                if (!open) { setOpen(true); return; }
                setActiveIndex((i) => step(i, e.key === "ArrowDown" ? 1 : -1));
                return;
            case "Enter":
            case " ":
                e.preventDefault();
                if (open) commit(options[activeIndex]);
                else setOpen(true);
                return;
            case "Escape":
                if (open) { e.preventDefault(); setOpen(false); }
                return;
            case "Home":
                if (open) { e.preventDefault(); setActiveIndex(step(-1, 1)); }
                return;
            case "End":
                if (open) { e.preventDefault(); setActiveIndex(step(0, -1)); }
                return;
        }
    };

    const SelectedIcon = selected?.icon;

    return (
        <div
            className={`ui-select ${className}`.trim()}
            ref={rootRef}
            onKeyDown={onKeyDown}
        >
            <button
                type="button"
                className={`ui-select-trigger ${open ? "open" : ""}`.trim()}
                aria-haspopup="listbox"
                aria-expanded={open}
                aria-label={ariaLabel}
                disabled={disabled}
                onClick={() => setOpen((o) => !o)}
            >
                <span className={`ui-select-value ${selected ? "" : "placeholder"}`.trim()}>
                    {SelectedIcon && <SelectedIcon size={16} />}
                    {selected ? (selected.text ?? selected.label) : placeholder}
                </span>
                <ChevronDown size={16} className="ui-select-caret" aria-hidden="true" />
            </button>

            {open && (
                <ul className="ui-select-list" role="listbox" aria-label={ariaLabel}>
                    {options.map((opt, i) => {
                        const Icon = opt.icon;
                        const isSelected = opt.value === value;
                        return (
                            <li
                                key={opt.value}
                                role="option"
                                aria-selected={isSelected}
                                aria-disabled={opt.disabled || undefined}
                                className={[
                                    "ui-select-option",
                                    isSelected ? "selected" : "",
                                    i === activeIndex ? "active" : "",
                                    opt.disabled ? "disabled" : "",
                                ].filter(Boolean).join(" ")}
                                onMouseEnter={() => setActiveIndex(i)}
                                onClick={() => commit(opt)}
                            >
                                {Icon && <Icon size={16} />}
                                <span className="ui-select-option-label">{opt.label}</span>
                                {isSelected && <Check size={15} className="tick" />}
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}

const Typed: <V extends string>(p: SelectMenuProps<V>) => React.ReactElement = SelectMenu;
export default Typed as FunctionComponent<SelectMenuProps<any>>;
